// Artemis Warehouse Inventory (card AIW-INVENTORY) — the list, on the aligned table.
//
// Jacky's column order, his location scheme, and his complaint that made the shape of this file:
// "please keep the CSS / UI clean i really hate disaligned, multiple row number". So every row is
// one line, every quantity is ONE number, and clicking that number opens the jobs and the movements
// behind it (screen 02 of the mockup) instead of stacking them into the cell.
//
// `free` is his Available — on hand − committed. Negative is a fact, not an error: it means material
// is committed that is not on the shelf.
import {useState} from 'react';
import {Link} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Page, Panel} from '../shell/Page';
import {cn} from '@aiw/ui';
import {DataTable, TableSkeleton, Quantity, Tag, Td} from '../components/aligned-table';
import {useT} from '../lib/i18n';
import {authHeaders, fetchImpl} from '../lib/api';
import {env} from '../lib/env';

export type StockRow = {
  variant_id: string; location: string; location_label: string | null; location_kind: string | null;
  product_id: string | null; variant_name: string | null; product_name: string | null;
  artemis_sku: string | null; supplier_sku: string | null; category: string | null;
  product_uom: string | null; description: string | null;
  on_hand: number; committed: number; ordered: number; available: number; available_incl_on_order: number;
  reorder_point: number | null; below_reorder: boolean;
};
export type WarehouseLocation = {code: string; kind: string; rack: string | null; level: number | null; position: number | null; label: string; active: boolean};
export type InventoryResponse = {data: StockRow[]; categories: {category: string; lines: number}[]; locations: WarehouseLocation[]; truncated?: boolean; read_limit?: number};
export type Detail = {
  commitments: {job_ref: string; qty: number; status: string}[];
  movements: {at: string; kind: string; qty: number; ref: string | null; note: string | null; by: string}[];
};

const n = (v: number) => v.toLocaleString('en-AU', {maximumFractionDigits: 2});

async function get<T>(path: string): Promise<T> {
  const r = await fetchImpl(`${env.apiBase}${path}`, {headers: await authHeaders()});
  const body = await r.json();
  if (!r.ok) throw Error(body.error ?? 'Unavailable');
  return body as T;
}

export function Inventory() {
  const {t} = useT();
  const [q, setQ] = useState('');
  const [location, setLocation] = useState<string | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [shortOnly, setShortOnly] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const search = new URLSearchParams();
  if (q) search.set('q', q);
  if (location) search.set('location', location);
  if (category) search.set('category', category);
  if (shortOnly) search.set('short', '1');
  const list = useQuery({
    queryKey: ['inventory', q, location, category, shortOnly],
    queryFn: () => get<InventoryResponse>(`/api/inventory${search.size ? `?${search}` : ''}`),
    refetchInterval: 60_000,
  });
  const detail = useQuery({
    queryKey: ['inventory-detail', open],
    queryFn: () => get<Detail>(`/api/inventory/${encodeURIComponent(open!)}`),
    enabled: !!open,
  });
  const rows = list.data?.data ?? [];
  const short = rows.filter((r) => r.available < 0);

  return <Page kicker={t('inv.kicker')} title={t('inv.title')} lede={t('inv.lede')}>
    {list.data?.truncated && <p role="status" className="mb-3 rounded-lg border bg-warning/10 px-4 py-2 text-sm">{t('inv.truncated', {limit: String(list.data.read_limit ?? 500)})}</p>}
    {short.length > 0 && <div role="status" className="mb-3 rounded-lg border border-l-4 border-l-warning bg-warning/10 px-4 py-2 text-sm">
      <b>{t('inv.shortTitle', {count: short.length})}</b>{' '}
      {short.slice(0, 3).map((r) => `${r.variant_name ?? r.artemis_sku} ${t('inv.by', {qty: n(Math.abs(r.available))})}`).join(' · ')}
    </div>}

    <div className="mb-3 flex flex-wrap items-center gap-2">
      <input className="h-8 w-64 rounded-md border bg-background px-2.5 text-sm" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={t('inv.search')} aria-label={t('inv.search')}/>
      <button type="button" onClick={() => setLocation(null)} className={cn('h-[26px] rounded-full border px-2.5 text-xs', location === null ? 'bg-primary text-primary-foreground' : 'bg-card')}>{t('inv.allLocations')}</button>
      {(list.data?.locations ?? []).filter((l) => l.kind !== 'carpet_rack').map((l) => (
        <button key={l.code} type="button" onClick={() => setLocation(l.code)}
          className={cn('h-[26px] rounded-full border px-2.5 text-xs', location === l.code ? 'bg-primary text-primary-foreground' : 'bg-card')}>{l.label}</button>
      ))}
      {(list.data?.categories ?? []).map((c) => (
        <button key={c.category} type="button" onClick={() => setCategory(category === c.category ? null : c.category)}
          className={cn('h-[26px] rounded-full border px-2.5 text-xs', category === c.category ? 'bg-primary text-primary-foreground' : 'bg-card')}>
          {c.category} <span className="tabular-nums opacity-70">{c.lines}</span>
        </button>
      ))}
      <button type="button" onClick={() => setShortOnly(!shortOnly)}
        className={cn('h-[26px] rounded-full border px-2.5 text-xs', shortOnly ? 'bg-warning text-warning-foreground' : 'bg-card')}>{t('inv.shortOnly')}</button>
    </div>

    {list.isError && <p role="alert" className="mb-3 text-sm text-warning">{t('inv.unavailable', {message: (list.error as Error).message})}</p>}

    <Panel title={t('inv.title')} meta={t('inv.meta')}>
      <DataTable cols={[
        {label: t('inv.col.sku'), width: 130, mono: true},
        {label: t('inv.col.product')},
        {label: t('inv.col.location'), width: 150},
        {label: t('inv.col.category'), width: 110},
        {label: t('inv.col.uom'), width: 56, mono: true},
        {label: t('inv.col.onHand'), width: 86, num: true},
        {label: t('inv.col.committed'), width: 96, num: true, hint: t('inv.committedHint')},
        {label: t('inv.col.ordered'), width: 84, num: true},
        {label: t('inv.col.available'), width: 96, num: true, hint: t('inv.availableHint')},
      ]} density="compact">
        {list.isPending && <TableSkeleton cols={9}/>}
        {rows.map((r) => (
          <tr key={`${r.variant_id}:${r.location}`}>
            <Td mono title={r.supplier_sku ? `${t('inv.col.supplierSku')}: ${r.supplier_sku}` : t('inv.noSupplierSku')}>{r.artemis_sku ?? '—'}</Td>
            <Td title={r.description ?? ''}>{r.variant_name ?? r.product_name ?? '—'}</Td>
            <Td><Tag mono>{r.location_label ?? r.location}</Tag></Td>
            <Td dim>{r.category ?? '—'}</Td>
            <Td mono dim>{r.product_uom ?? '—'}</Td>
            <Td num>{n(r.on_hand)}</Td>
            <Td num><Quantity value={r.committed} onOpen={() => setOpen(open === r.variant_id ? null : r.variant_id)}/></Td>
            <Td num dim>{r.ordered ? n(r.ordered) : '—'}</Td>
            <Td num className={r.available < 0 ? 'text-warning' : undefined}>{n(r.available)}</Td>
          </tr>
        ))}
        {list.data && rows.length === 0 && <tr><Td colSpan={9} className="py-6 text-center" dim>{t('inv.none')}</Td></tr>}
      </DataTable>
      <p className="mt-2 text-xs text-muted-foreground">{t('inv.footnote')}</p>
    </Panel>

    {open && (
      <Panel title={t('inv.intel')} meta={rows.find((r) => r.variant_id === open)?.variant_name ?? ''} className="mt-4">
        {detail.isPending && <p role="status" className="text-sm">{t('inv.loading')}</p>}
        {detail.data && <div className="grid gap-4 md:grid-cols-3">
          <div>
            <h4 className="mb-1 text-micro uppercase tracking-wider text-muted-foreground">{t('inv.commitments')}</h4>
            <DataTable cols={[{label: t('inv.col.job')}, {label: t('inv.col.qty'), width: 60, num: true}]} density="compact">
              {detail.data.commitments.map((c, i) => (
                <tr key={`${c.job_ref}-${i}`}>
                  <Td><Link className="text-primary underline" to={`/jobs/${c.job_ref}`}>{c.job_ref}</Link></Td>
                  <Td num>{n(c.qty)}</Td>
                </tr>
              ))}
              {detail.data.commitments.length === 0 && <tr><Td colSpan={2} dim>{t('inv.noCommitments')}</Td></tr>}
            </DataTable>
          </div>
          <div className="md:col-span-2">
            <h4 className="mb-1 text-micro uppercase tracking-wider text-muted-foreground">{t('inv.movements')}</h4>
            <DataTable cols={[{label: t('inv.col.when'), width: 110}, {label: t('inv.col.kind'), width: 110}, {label: t('inv.col.qty'), width: 70, num: true}, {label: t('inv.col.ref')}]} density="compact">
              {detail.data.movements.map((m, i) => (
                <tr key={i}>
                  <Td dim>{new Date(m.at).toLocaleDateString('en-AU', {day: '2-digit', month: 'short', timeZone: 'Australia/Sydney'})}</Td>
                  <Td>{m.kind}</Td>
                  <Td num className={m.qty < 0 ? 'text-warning' : undefined}>{n(m.qty)}</Td>
                  <Td dim>{m.ref ?? m.note ?? '—'}</Td>
                </tr>
              ))}
              {detail.data.movements.length === 0 && <tr><Td colSpan={4} dim>{t('inv.noMovements')}</Td></tr>}
            </DataTable>
          </div>
        </div>}
      </Panel>
    )}

    <Panel title={t('inv.rackTitle')} meta={t('inv.rackMeta')} className="mt-4">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {(list.data?.locations ?? []).filter((l) => l.kind === 'carpet_rack').map((l) => (
          <button key={l.code} type="button" onClick={() => setLocation(location === l.code ? null : l.code)}
            className={cn('rounded-md border p-2 text-left text-xs', location === l.code ? 'border-primary bg-primary/10' : 'bg-card')}>
            <b className="block font-mono text-xs text-primary">{l.code}</b>
            <span className="text-muted-foreground">{t('inv.levelPos', {level: l.level ?? 0, position: l.position ?? 0})}</span>
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{t('inv.rackFootnote')}</p>
    </Panel>
  </Page>;
}
